import {useAuth} from '@apps-common/auth-react';
import {Box} from '@mui/material';
import isEqual from 'lodash/isEqual';
import {containsExtent} from 'ol/extent';
import {GeoJSON} from 'ol/format';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef} from 'react';
import stickymap from 'stickymap';

import NoPreview from './thumbnail-no-preview.jsx';
import {TILES_BASE_URL} from '@explorer/config';
import {itemTypeSupported} from '@explorer/util/items';

const THUMB_DELAY = 150;
const MAX_COMPOSITE_SIZE = 10; // composition limit on the tile service

const Thumbnail = ({
  sx,
  ariaLabel,
  checkIntersect,
  clip,
  item,
  height = 60,
  width = 60,
  items: propsItems,
  useWebp,
  ...props
}) => {
  const auth = useAuth();

  const timeoutRef = useRef();
  const thumbnailContainerRef = useRef();

  const itemsArray = React.useMemo(() => {
    const items = item ? [item] : propsItems;
    return items.filter(item => itemTypeSupported(item.properties.item_type));
  }, [propsItems, item]);

  const renderThumbnail = useCallback(() => {
    const ext = useWebp ? 'webp' : 'png';
    const layers = [];
    const idsByItemType = {};

    itemsArray
      .slice()
      .reverse()
      .forEach(item => {
        const type = item.properties.item_type;

        if (!(type in idsByItemType)) {
          idsByItemType[type] = [];
        }
        idsByItemType[type].push(item.properties.item_id || item.id);
      });

    const footprint = {
      type: 'GeometryCollection',
      geometries: itemsArray.map(i => i.geometry),
    };

    const footprintExtent = new GeoJSON().readGeometry(footprint).getExtent();
    const clipExtent = clip && new GeoJSON().readGeometry(clip).getExtent();

    const footprintWithinClip =
      checkIntersect &&
      clipExtent &&
      containsExtent(clipExtent, footprintExtent);

    const extent = !clip || footprintWithinClip ? footprintExtent : clipExtent;

    const bbox = extent.join(',');
    for (const type in idsByItemType) {
      const ids = idsByItemType[type];

      for (let i = 0, ii = ids.length; i < ii; i += MAX_COMPOSITE_SIZE) {
        const compositeId = ids.slice(i, i + MAX_COMPOSITE_SIZE).join(',');
        layers.push({
          bbox: extent,
          untiled: true,
          url: `${TILES_BASE_URL}/data/v1/${type}/${compositeId}/thumb?format=${ext}&bbox=${bbox}&fit=true&token=${auth.user?.accessToken}`,
        });
      }
    }

    const config = {
      layers: layers,
      height,
      width,
    };

    if (clip && !footprintWithinClip) {
      config.clip = clip;
    } else {
      config.clip = footprint;
    }

    const node = thumbnailContainerRef.current;
    if (node && node.children.length) {
      node.innerHTML = '';
    }

    const map = stickymap(config);

    if (ariaLabel) {
      map.setAttribute('aria-label', ariaLabel);
      map.setAttribute('role', 'img');
    }

    node.appendChild(map);
  }, [
    useWebp,
    itemsArray,
    clip,
    checkIntersect,
    height,
    width,
    ariaLabel,
    auth.user?.accessToken,
  ]);

  const init = () => {
    if (itemsArray.length) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(renderThumbnail, THUMB_DELAY);
      return () => clearTimeout(timeoutRef.current);
    }
  };

  useEffect(init, [thumbnailContainerRef, renderThumbnail, itemsArray.length]);

  if (!itemsArray.length) {
    return <NoPreview sx={[...(Array.isArray(sx) ? sx : [sx])]} />;
  }

  return (
    <Box
      ref={thumbnailContainerRef}
      sx={[
        {
          width: '100%',
          height: '100%',
          '& canvas': {
            display: 'block',
            width: '100%',
            height: '100%',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    />
  );
};

Thumbnail.propTypes = {
  ariaLabel: PropTypes.string,
  checkIntersect: PropTypes.bool,
  clip: PropTypes.object,
  height: PropTypes.number,
  item: PropTypes.object,
  items: PropTypes.array,
  useWebp: PropTypes.bool,
  width: PropTypes.number,
  sx: PropTypes.object,
};

const checkForRerender = (prevProps, nextProps) => {
  const simpleProps = ['height', 'width', 'useWebp', 'checkIntersect'];
  let isDiff = false;
  simpleProps.forEach(propName => {
    if (!isDiff && prevProps[propName] !== nextProps[propName]) {
      isDiff = true;
    }
  });
  if (!isDiff) {
    isDiff =
      !isEqual(prevProps.clip, nextProps.clip) ||
      !isEqual(prevProps.items, nextProps.items) ||
      !isEqual(prevProps.item, nextProps.item);
  }
  return !isDiff;
};

export default React.memo(Thumbnail, checkForRerender);
